import * as React from 'react';
import type {
	IOption,
	SelectOpenInteraction,
	IUseSelectStateProps,
	IUseSelectStateResult,
	SelectOptionValue,
	SelectSelectionMode,
} from '../types';
import {
	getEnabledOptionIndexes,
	getInitialHighlightedIndex,
	getNextHighlightedIndex,
	getResolvedHighlightedIndex,
	NO_HIGHLIGHTED_OPTION_INDEX,
} from './selectState.utils';
import { useSelectKeyboard } from './useSelectKeyboard';

const DEFAULT_SELECTION_MODE: SelectSelectionMode = 'single';

const hasSelectValueChanged = <TValue extends SelectOptionValue>(
	previousValue: ReadonlyArray<TValue>,
	value: ReadonlyArray<TValue>
): boolean =>
	previousValue.length !== value.length || previousValue.some((selectedValue, index) => selectedValue !== value[index]);

const hasSelectOptionsChanged = <TValue extends SelectOptionValue, TMetadata>(
	previousOptions: ReadonlyArray<IOption<TValue, TMetadata>>,
	options: ReadonlyArray<IOption<TValue, TMetadata>>
): boolean =>
	previousOptions.length !== options.length ||
	previousOptions.some((previousOption, index) => {
		const option = options[index];
		return !option || previousOption.value !== option.value || Boolean(previousOption.disabled) !== Boolean(option.disabled);
	});

/**
 * Manages select visibility, highlighted options, selection changes, and keyboard interactions.
 */
export const useSelectState = <TValue extends SelectOptionValue = SelectOptionValue, TMetadata = unknown>({
	defaultOpen = false,
	disabled = false,
	isOpen: controlledIsOpen,
	loading = false,
	onChange,
	onOpenChange,
	options,
	selectionMode = DEFAULT_SELECTION_MODE,
	value,
}: IUseSelectStateProps<TValue, TMetadata>): IUseSelectStateResult<TValue, TMetadata> => {
	const [internalIsOpen, setInternalIsOpen] = React.useState(defaultOpen);
	const [highlightedIndex, setHighlightedIndexState] = React.useState(NO_HIGHLIGHTED_OPTION_INDEX);
	const [deferInitialHighlight, setDeferInitialHighlight] = React.useState(false);
	const [isOptionHighlightSuppressed, setIsOptionHighlightSuppressed] = React.useState(false);
	const [hasArrowNavigation, setHasArrowNavigation] = React.useState(false);
	const previousHighlightedIndexRef = React.useRef(NO_HIGHLIGHTED_OPTION_INDEX);
	const previousHighlightedValueRef = React.useRef<TValue | undefined>(undefined);
	const previousOptionsRef = React.useRef(options);
	const previousValueRef = React.useRef(value);
	const previousLoadingRef = React.useRef(loading);
	const preserveDeferredHighlightAfterLoadingRef = React.useRef(false);
	const pendingHighlightedIndexOnOpenRef = React.useRef<number | undefined>(undefined);
	const preferredHighlightedIndexOnValueChangeRef = React.useRef<number | undefined>(undefined);
	const isOpenControlled = controlledIsOpen !== undefined;
	const isOpen = controlledIsOpen ?? internalIsOpen;

	const enabledOptionIndexes = React.useMemo(() => getEnabledOptionIndexes(options), [options]);

	const selectedOptions = React.useMemo(
		() => options.filter(option => value.includes(option.value)),
		[options, value]
	);
	const selectedEnabledOptionIndex = React.useMemo(
		() => options.findIndex(option => !option.disabled && value.includes(option.value)),
		[options, value]
	);

	const optionsChanged = hasSelectOptionsChanged(previousOptionsRef.current, options);
	const optionsChangedAfterLoading = previousLoadingRef.current && !loading;
	const preserveDeferredHighlightAfterLoading = preserveDeferredHighlightAfterLoadingRef.current && !loading;
	const valueChanged = hasSelectValueChanged(previousValueRef.current, value);
	const previousHighlightedValue = previousHighlightedValueRef.current;

	const updateOpenState = React.useCallback(
		(nextIsOpen: boolean) => {
			if (disabled && nextIsOpen) {
				return;
			}
			if (!isOpenControlled) {
				setInternalIsOpen(nextIsOpen);
			}
			if (nextIsOpen !== isOpen) {
				onOpenChange?.(nextIsOpen);
			}
		},
		[disabled, isOpen, isOpenControlled, onOpenChange]
	);

	const setHighlightedIndex = React.useCallback(
		(index: number) => {
			if (index === NO_HIGHLIGHTED_OPTION_INDEX || (options[index] && !options[index].disabled)) {
				preserveDeferredHighlightAfterLoadingRef.current = false;
				setDeferInitialHighlight(false);
				setIsOptionHighlightSuppressed(false);
				setHighlightedIndexState(index);
			}
		},
		[options]
	);

	const clearHighlightedOption = React.useCallback(() => {
		preserveDeferredHighlightAfterLoadingRef.current = false;
		setDeferInitialHighlight(false);
		setIsOptionHighlightSuppressed(true);
		setHighlightedIndexState(NO_HIGHLIGHTED_OPTION_INDEX);
	}, []);

	const initialHighlightedIndex = React.useMemo(
		() => getInitialHighlightedIndex({ enabledOptionIndexes, options, value }),
		[enabledOptionIndexes, options, value]
	);

	const highlightFirstOption = React.useCallback(() => {
		preserveDeferredHighlightAfterLoadingRef.current = false;
		setDeferInitialHighlight(false);
		setIsOptionHighlightSuppressed(false);
		setHighlightedIndexState(enabledOptionIndexes[0] ?? NO_HIGHLIGHTED_OPTION_INDEX);
	}, [enabledOptionIndexes]);

	const resolvedHighlightedIndex =
		isOpen && isOptionHighlightSuppressed
			? NO_HIGHLIGHTED_OPTION_INDEX
			: getResolvedHighlightedIndex({
					deferInitialHighlight,
					highlightedIndex,
					initialHighlightedIndex,
					isOpen,
					options,
					optionsChanged,
					pendingHighlightedIndexOnOpen: pendingHighlightedIndexOnOpenRef.current,
					preserveDeferredHighlightOnOptionsChange:
						optionsChangedAfterLoading || preserveDeferredHighlightAfterLoading,
					preferredHighlightedIndexOnValueChange: preferredHighlightedIndexOnValueChangeRef.current,
					previousHighlightedIndex: previousHighlightedIndexRef.current,
					previousHighlightedValue,
					valueChanged,
				});
	const highlightedOption =
		resolvedHighlightedIndex === NO_HIGHLIGHTED_OPTION_INDEX ? undefined : options[resolvedHighlightedIndex];

	const openSelect = React.useCallback(
		(interaction: SelectOpenInteraction = 'keyboard') => {
			if (disabled) {
				return;
			}
			const shouldDeferInitialHighlight = interaction === 'pointer';
			const nextHighlightedIndex = shouldDeferInitialHighlight
				? NO_HIGHLIGHTED_OPTION_INDEX
				: initialHighlightedIndex;
			preserveDeferredHighlightAfterLoadingRef.current = shouldDeferInitialHighlight && loading;
			setHasArrowNavigation(false);
			setIsOptionHighlightSuppressed(false);
			if (isOpenControlled && !isOpen) {
				pendingHighlightedIndexOnOpenRef.current = nextHighlightedIndex;
			}
			setDeferInitialHighlight(shouldDeferInitialHighlight);
			setHighlightedIndexState(nextHighlightedIndex);
			updateOpenState(true);
		},
		[disabled, initialHighlightedIndex, isOpen, isOpenControlled, loading, updateOpenState]
	);

	const closeSelect = React.useCallback(() => {
		preserveDeferredHighlightAfterLoadingRef.current = false;
		pendingHighlightedIndexOnOpenRef.current = undefined;
		setHasArrowNavigation(false);
		setDeferInitialHighlight(false);
		setIsOptionHighlightSuppressed(false);
		setHighlightedIndexState(NO_HIGHLIGHTED_OPTION_INDEX);
		updateOpenState(false);
	}, [updateOpenState]);

	const toggleSelect = React.useCallback(() => {
		if (isOpen) {
			closeSelect();
			return;
		}
		openSelect('pointer');
	}, [closeSelect, isOpen, openSelect]);

	const selectOption = React.useCallback(
		(option: IOption<TValue, TMetadata>) => {
			if (disabled || option.disabled) {
				return;
			}

			const selectedOptionIndex = options.findIndex(candidateOption => candidateOption.value === option.value);
			preserveDeferredHighlightAfterLoadingRef.current = false;
			setDeferInitialHighlight(false);
			setIsOptionHighlightSuppressed(false);
			setHighlightedIndexState(selectedOptionIndex);
			const isSelected = value.includes(option.value);
			if (selectionMode === 'multiple') {
				preferredHighlightedIndexOnValueChangeRef.current = selectedOptionIndex;
				const nextValue = isSelected
					? value.filter(selectedValue => selectedValue !== option.value)
					: [...value, option.value];
				onChange(nextValue, {
					action: isSelected ? 'deselect' : 'select',
					option,
					selectionMode,
				});
				return;
			}

			onChange([option.value], { action: 'select', option, selectionMode });
			closeSelect();
		},
		[closeSelect, disabled, onChange, options, selectionMode, value]
	);

	const clearSelection = React.useCallback(() => {
		if (disabled || value.length === 0) {
			return;
		}
		onChange([], { action: 'clear', selectionMode });
	}, [disabled, onChange, selectionMode, value.length]);

	const moveHighlight = React.useCallback(
		(offset: number) => {
			const highlightedIndexForNavigation =
				deferInitialHighlight && resolvedHighlightedIndex === NO_HIGHLIGHTED_OPTION_INDEX
					? selectedEnabledOptionIndex
					: resolvedHighlightedIndex;
			const nextHighlightedIndex = getNextHighlightedIndex({
				enabledOptionIndexes,
				highlightedIndex: highlightedIndexForNavigation,
				offset,
			});
			if (isOpenControlled && !isOpen) {
				pendingHighlightedIndexOnOpenRef.current = nextHighlightedIndex;
			}
			preserveDeferredHighlightAfterLoadingRef.current = false;
			setDeferInitialHighlight(false);
			setIsOptionHighlightSuppressed(false);
			setHighlightedIndexState(nextHighlightedIndex);
		},
		[
			deferInitialHighlight,
			enabledOptionIndexes,
			isOpen,
			isOpenControlled,
			resolvedHighlightedIndex,
			selectedEnabledOptionIndex,
		]
	);

	const showHighlight = React.useCallback(() => {
		setHasArrowNavigation(true);
	}, []);

	const hideHighlight = React.useCallback(() => {
		setHasArrowNavigation(false);
	}, []);

	const handleKeyDown = useSelectKeyboard({
		closeSelect,
		disabled,
		enabledOptionIndexes,
		highlightedOption,
		isOpen,
		moveHighlight,
		openSelect,
		selectOption,
		setHighlightedIndex,
		showHighlight,
	});

	React.useEffect(() => {
		if (resolvedHighlightedIndex !== highlightedIndex) {
			setHighlightedIndexState(resolvedHighlightedIndex);
		}
		if (
			deferInitialHighlight &&
			optionsChanged &&
			resolvedHighlightedIndex !== NO_HIGHLIGHTED_OPTION_INDEX
		) {
			setDeferInitialHighlight(false);
		}

		if (
			deferInitialHighlight &&
			isOpen &&
			loading &&
			resolvedHighlightedIndex === NO_HIGHLIGHTED_OPTION_INDEX
		) {
			preserveDeferredHighlightAfterLoadingRef.current = true;
		}
		if (
			!isOpen ||
			!deferInitialHighlight ||
			valueChanged ||
			(preserveDeferredHighlightAfterLoadingRef.current && !loading && optionsChanged && options.length > 0)
		) {
			preserveDeferredHighlightAfterLoadingRef.current = false;
		}

		previousOptionsRef.current = options;
		previousValueRef.current = value;
		previousLoadingRef.current = loading;
		previousHighlightedIndexRef.current = resolvedHighlightedIndex;
		previousHighlightedValueRef.current = options[resolvedHighlightedIndex]?.value;
		if (valueChanged) {
			preferredHighlightedIndexOnValueChangeRef.current = undefined;
		}
		if (isOpen) {
			pendingHighlightedIndexOnOpenRef.current = undefined;
		}
	}, [
		deferInitialHighlight,
		highlightedIndex,
		isOpen,
		loading,
		options,
		optionsChanged,
		resolvedHighlightedIndex,
		value,
		valueChanged,
	]);

	React.useEffect(() => {
		if (disabled && isOpen) {
			closeSelect();
		}
	}, [closeSelect, disabled, isOpen]);

	return {
		clearHighlightedOption,
		clearSelection,
		closeSelect,
		handleKeyDown,
		hideHighlight,
		highlightFirstOption,
		highlightedIndex: resolvedHighlightedIndex,
		highlightedOption,
		isHighlightVisible: hasArrowNavigation && resolvedHighlightedIndex !== NO_HIGHLIGHTED_OPTION_INDEX,
		isOpen,
		openSelect,
		selectOption,
		selectedOptions,
		setHighlightedIndex,
		showHighlight,
		toggleSelect,
	};
};
